import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { familyAPI } from '../services/api';
import { useAuth } from './AuthContext';

export interface InvitationInfo {
  family_name: string;
  invited_by: string;
  family_id: string;
  subscription_status?: string;
  message?: string;
  // Target email the invite was addressed to. Required for the
  // email-mismatch hard block on InvitationWelcome.
  email?: string;
  // Role the inviter selected on send. When present we honor it on accept
  // (set users.user_type) and route guardians inline to the dashboard
  // instead of through the athlete-shaped quickstart.
  anticipated_user_type?: 'guardian' | 'athlete' | null;
}

// Backend pre-flight returns the page the invite-link click should land on.
// Drives the InvitationRouter so we never show "Join Family" intermediate
// screens for fresh signups, and never dead-end existing-different-family
// users on a generic 400.
export type InvitationDestination =
  | 'signup'
  | 'login'
  | 'login_for_existing_member'
  | 'login_then_family_conflict'
  | 'auto_accept'
  | 'invitation_welcome_softwarn'
  | 'family_conflict'
  | 'already_member_redirect';

export interface InvitationPreflight {
  destination: InvitationDestination;
  invitation: {
    id: string;
    family_id: string;
    family_name: string;
    inviter_name?: string | null;
    email: string;
    anticipated_user_type?: 'guardian' | 'athlete' | null;
    expires_at: string;
  };
  context: {
    user_exists: boolean;
    session_matches_invite: boolean;
    current_in_this_family: boolean;
    current_in_different_family: boolean;
    current_family_id: string | null;
  };
}

export interface InvitationContextType {
  invitationToken: string | null;
  invitationInfo: InvitationInfo | null;
  isProcessing: boolean;
  error: string | null;
  isValidating: boolean;
  validateInvitation: (token: string) => Promise<boolean>;
  acceptInvitation: (
    sessionToken: string,
    userData: {
      name: string;
      email: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
      selected_user_type?: 'guardian' | 'athlete' | null;
    },
  ) => Promise<{
    ok: boolean;
    user_type?: string | null;
    email_mismatch?: boolean;
    code?: string;
    message?: string;
  }>;
  clearInvitation: () => void;
  clearError: () => void;
  resetState: () => void;
}

const InvitationContext = createContext<InvitationContextType | undefined>(undefined);

export const useInvitation = (): InvitationContextType => {
  const context = useContext(InvitationContext);
  if (!context) {
    throw new Error('useInvitation must be used within an InvitationProvider');
  }
  return context;
};

interface InvitationProviderProps {
  children: ReactNode;
}

export const InvitationProvider: React.FC<InvitationProviderProps> = ({ children }) => {
  const { registerLogoutCallback } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [state, setState] = useState({
    invitationToken: null as string | null,
    invitationInfo: null as InvitationInfo | null,
    isProcessing: false,
    error: null as string | null,
    isValidating: false,
  });

  // Initialize from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('invitation_token');
    const storedInfo = localStorage.getItem('invitation_info');
    
    if (storedToken && storedInfo) {
      try {
        const parsedInfo = JSON.parse(storedInfo);
        setState(prev => ({
          ...prev,
          invitationToken: storedToken,
          invitationInfo: parsedInfo,
        }));
        console.log('🎫 Restored invitation from localStorage:', storedToken);
      } catch (error) {
        console.error('Failed to parse stored invitation:', error);
        localStorage.removeItem('invitation_token');
        localStorage.removeItem('invitation_info');
      }
    }
  }, []);

  // Check URL for invitation token on mount and when URL changes
  useEffect(() => {
    // Check query parameters first. A college coach invite link
    // (/signup?coach=1&invite=...) carries its own token, which is not a
    // family invitation: validating it here would fail, and until it failed it
    // would hide the coach page's switch and ride along on sign-up as a family
    // invite_token. SignUpPage handles that token itself.
    const coachLink = searchParams.get('coach') === '1';
    const inviteToken = coachLink ? null : (searchParams.get('invite') || searchParams.get('token'));
    
    // Check path for /invite/{token} pattern
    const path = window.location.pathname;
    const invitePathMatch = path.match(/\/invite\/([a-zA-Z0-9_-]+)/);
    const inviteHtmlMatch = path.includes('invite.html');
    
    // Determine token from various sources
    let token = inviteToken;
    if (!token && invitePathMatch) {
      token = invitePathMatch[1];
    }
    if (!token && inviteHtmlMatch && searchParams.get('token')) {
      token = searchParams.get('token');
    }
    
    if (token && token !== state.invitationToken) {
      console.log('🎫 Invitation token detected:', token);
      setState(prev => ({
        ...prev,
        invitationToken: token,
        error: null,
      }));
      
      // Persist token to localStorage so it survives navigation to /login
      localStorage.setItem('invitation_token', token);
      
      validateInvitation(token);
    }
  }, [searchParams, window.location.pathname]);

  // Register logout callback to reset state
  useEffect(() => {
    registerLogoutCallback(() => {
      resetState();
    });
  }, [registerLogoutCallback]);

  const validateInvitation = async (token: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isValidating: true, error: null }));

    try {
      const response = await familyAPI.validateInvitation(token);

      if (response.valid && response.invitation) {
        const invitationInfo: InvitationInfo = {
          family_name: response.invitation.family.name,
          invited_by: response.invitation.invited_by.name || response.invitation.invited_by.email,
          family_id: response.invitation.family.id,
          message: response.invitation.message,
          email: response.invitation.email,
          anticipated_user_type: response.invitation.anticipated_user_type,
        };
        
        setState(prev => ({
          ...prev,
          invitationInfo,
          isValidating: false,
          error: null,
        }));
        
        // Persist invitation info to localStorage
        localStorage.setItem('invitation_info', JSON.stringify(invitationInfo));
        
        return true;
      } else {
        // Invalid/expired token — purge it (and any stale info) from
        // localStorage AND state. Otherwise the dead token is restored from
        // storage on every future mount/login, re-validates, re-fails, and
        // keeps the "invalid invitation" error riding along indefinitely.
        localStorage.removeItem('invitation_token');
        localStorage.removeItem('invitation_info');
        setState(prev => ({
          ...prev,
          invitationToken: null,
          invitationInfo: null,
          isValidating: false,
          error: 'Invalid or expired invitation',
        }));
        return false;
      }
    } catch (error: any) {
      console.error('Invitation validation error:', error);
      setState(prev => ({
        ...prev,
        isValidating: false,
        error: error.message || 'Failed to validate invitation',
      }));
      return false;
    }
  };

  const acceptInvitation = async (
    sessionToken: string,
    userData: {
      name: string;
      email: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
      selected_user_type?: 'guardian' | 'athlete' | null;
    },
  ): Promise<{ ok: boolean; user_type?: string | null; email_mismatch?: boolean; code?: string; message?: string }> => {
    if (!state.invitationToken) {
      setState(prev => ({ ...prev, error: 'No invitation token' }));
      return { ok: false, code: 'MISSING_TOKEN' };
    }

    setState(prev => ({ ...prev, isProcessing: true, error: null }));

    try {
      const response = await familyAPI.acceptInvitation(
        state.invitationToken,
        userData,
        sessionToken
      );

      if (response.success) {
        setState(prev => ({
          ...prev,
          isProcessing: false,
          error: null,
        }));

        // Remove invite token from URL
        searchParams.delete('invite');
        searchParams.delete('token');
        setSearchParams(searchParams);

        return {
          ok: true,
          user_type: response.user_type ?? null,
          email_mismatch: response.email_mismatch ?? false,
        };
      } else {
        setState(prev => ({
          ...prev,
          isProcessing: false,
          error: 'Failed to accept invitation',
        }));
        return { ok: false };
      }
    } catch (error: any) {
      console.error('Accept invitation error:', error);
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: error.message || 'Failed to accept invitation',
      }));
      // apiCall attaches the backend's structured error code on thrown.code
      // so the caller can branch on FAMILY_CONFLICT / FAMILY_FULL /
      // INVITATION_EXPIRED / INVITATION_USED instead of guessing.
      return { ok: false, code: error?.code, message: error?.message };
    }
  };

  const clearInvitation = () => {
    setState(prev => ({
      ...prev,
      invitationToken: null,
      invitationInfo: null,
      error: null,
    }));
    
    // Remove from localStorage
    localStorage.removeItem('invitation_token');
    localStorage.removeItem('invitation_info');
    
    // Remove invite token from URL
    searchParams.delete('invite');
    searchParams.delete('token');
    setSearchParams(searchParams);
  };

  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  const resetState = () => {
    setState({
      invitationToken: null,
      invitationInfo: null,
      isProcessing: false,
      error: null,
      isValidating: false,
    });
    
    // Clear from localStorage
    localStorage.removeItem('invitation_token');
    localStorage.removeItem('invitation_info');
  };

  const value: InvitationContextType = {
    ...state,
    validateInvitation,
    acceptInvitation,
    clearInvitation,
    clearError,
    resetState,
  };

  return <InvitationContext.Provider value={value}>{children}</InvitationContext.Provider>;
};
